import { useId } from "react";
import t from "@/lang/lang";
import { EntityId } from "@/shared/types/entity-id";
import { Kind } from "@/services/backend/samples/types/kind";
import { Laboratory } from "@/services/backend/samples/types/laboratory";
import { useSamplesLaboratoryListQuery } from "@/services/backend/samples/api/lab-api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Field,
  FieldDescription,
  FieldLabel,
} from "@/shared/components/ui/field";
import { FieldErrors, FieldProps } from "@/shared/components/field/field";

/**
 * Radix does not allow an empty item value, so "no default" is selected
 * through this placeholder and reported as null.
 */
const noDefault = "none";

/**
 * Selects the lab samples of the kind are sent to by default. Only labs
 * analysing the kind are offered, the backend rejects any other. "Kein
 * Standard" leaves the lab to be chosen per sample.
 */
export function DefaultLabField({
  kind,
  labId,
  onLabIdChange,
  label = t("Standardlabor"),
  description = t(
    "Neue Proben werden diesem Labor zugeordnet. Es werden nur Labore angeboten, die diese Probenart analysieren.",
  ),
  errors,
  fieldProps,
  disabled,
}: {
  kind: Kind;
  labId: EntityId;
  onLabIdChange: (labId: EntityId) => void;
  disabled?: boolean;
} & FieldProps) {
  const id = useId();
  const { data } = useSamplesLaboratoryListQuery({});
  const labs = (data?.labs ?? []).filter((l) => analyses(l, kind));

  return (
    <Field {...fieldProps}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Select
        value={labId ?? noDefault}
        onValueChange={(v) => onLabIdChange(v === noDefault ? null : v)}
        disabled={disabled || !data}
      >
        <SelectTrigger id={id} aria-invalid={errors && errors.length > 0}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={noDefault}>{t("Kein Standard")}</SelectItem>
          {labs.map((l) => (
            <SelectItem value={l.id} key={l.id}>
              {l.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {description && <FieldDescription>{description}</FieldDescription>}
      <FieldErrors errors={errors} />
    </Field>
  );
}

/** Mirrors `sample.LabAnalyses` in the backend. */
function analyses(lab: Laboratory, kind: Kind): boolean {
  switch (kind) {
    case Kind.AirMicrobial:
      return lab.analysesAirMicrobialSamples;
    case Kind.SurfaceMicrobial:
      return lab.analysesSurfaceMicrobialSamples;
    case Kind.DustDensity:
      return lab.analysesDustDensitySamples;
    case Kind.Water:
      return lab.analysesWaterSamples;
    default:
      return false;
  }
}
